/**
 * AudioWorklet processor using WASM bytecode executor
 * Preallocates buffers during init and reuses them for hot-reloading
 */

import type * as WasmExports from '../as/build/index.d.ts'
import config from '../asconfig.json'
import { BufferReader, type SharedBuffers } from './buffer-manager.ts'
import { ControlIndex, RING_BUFFER_SIZE } from './bytecode.ts'
import { rpc } from './lib/rpc.ts'
import { type WasmSetup, wasmSetup } from './lib/wasm-setup.ts'

const BLOCK_SIZE = 128

declare const sampleRate: number

interface SampleData {
  sampleId: number
  data: ArrayBuffer
  length: number
  contentHash: number
}

interface IRData {
  irId: number
  data: ArrayBuffer
  length: number
}

interface SequenceData {
  data: Float64Array
  slotIndex: number
}

interface SingleProgramData {
  bytecode: Float64Array
  sequences: SequenceData[]
  samples: SampleData[]
  impulseResponses: IRData[]
  bpm: number
  hasExplicitBpm: boolean
  tune: number
}

interface DualProgramData {
  programA: SingleProgramData
  programB: SingleProgramData
  globalBpm: number
  crossfade: number
  tune: number
}

class Limiter {
  private envL = 0.0
  private envR = 0.0
  private threshold = 1.0
  private attackCoeff = 0.0
  private releaseCoeff = 0.0

  constructor(sampleRate: number) {
    const attackTime = 0.0001
    const releaseTime = 0.01
    this.attackCoeff = Math.exp(-1 / (attackTime * sampleRate))
    this.releaseCoeff = Math.exp(-1 / (releaseTime * sampleRate))
  }

  process(left: Float32Array, right: Float32Array, blockSize: number): void {
    for (let i = 0; i < blockSize; i++) {
      const leftIn = left[i]
      const rightIn = right[i]
      const leftLevel = Math.abs(leftIn)
      const rightLevel = Math.abs(rightIn)

      if (leftLevel > this.envL) {
        this.envL = this.attackCoeff * (this.envL - leftLevel) + leftLevel
      }
      else {
        this.envL = this.releaseCoeff * (this.envL - leftLevel) + leftLevel
      }

      const gainL = this.envL > this.threshold ? this.threshold / this.envL : 1.0
      left[i] = Math.atan(leftIn * gainL * 1.1)

      if (rightLevel > this.envR) {
        this.envR = this.attackCoeff * (this.envR - rightLevel) + rightLevel
      }
      else {
        this.envR = this.releaseCoeff * (this.envR - rightLevel) + rightLevel
      }

      const gainR = this.envR > this.threshold ? this.threshold / this.envR : 1.0
      right[i] = Math.atan(rightIn * gainR * 1.1)
    }
  }
}

class Downsample {
  process(
    input: Float32Array,
    output: Float32Array,
    outputSize: number,
    factor: number,
  ): void {
    // Simple boxcar (moving average) filter - naturally acts as low-pass
    // This preserves low frequencies perfectly (DC gain = 1.0)
    // The averaging itself provides anti-aliasing
    for (let i = 0; i < outputSize; i++) {
      let sum = 0.0
      const startIdx = i * factor
      for (let j = 0; j < factor; j++) {
        sum += input[startIdx + j]
      }
      output[i] = sum / factor
    }
  }
}

class loopmasterProcessorWasm extends (globalThis.AudioWorkletProcessor || class {}) {
  private wasm: WasmSetup<typeof WasmExports> | null = null
  private reader: BufferReader | null = null
  private leftBuffer: Float32Array | null = null
  private rightBuffer: Float32Array | null = null
  private leftBufferA: Float32Array | null = null
  private rightBufferA: Float32Array | null = null
  private leftBufferB: Float32Array | null = null
  private rightBufferB: Float32Array | null = null
  private programBufferPtr = 0
  private programBufferPtrA = 0
  private programBufferPtrB = 0
  private programBufferSize = 0
  private sequenceBufferPtrs: number[] = []
  private sequenceBufferSize = 0
  private control: Int32Array | null = null
  private workletProgram: Float64Array | null = null
  private workletProgramA: Float64Array | null = null
  private workletProgramB: Float64Array | null = null
  private workletSequences: Float64Array[] = []
  private globalSampleCounter = 0
  private dualChannelLoggedOnce = false
  private lastBpm = -1
  private lastBpmA = -1
  private lastBpmB = -1
  private lastTuneA = -1
  private lastTuneB = -1
  private limiter: Limiter | null = null
  private leftMixed: Float32Array | null = null
  private rightMixed: Float32Array | null = null
  private currentOversampling = 1
  private leftOversampledBuffer: Float32Array | null = null
  private rightOversampledBuffer: Float32Array | null = null
  private leftOversampledBufferPtr = 0
  private rightOversampledBufferPtr = 0
  private downsample: Downsample | null = null
  private downsampleOutputBuffer: Float32Array | null = null
  private ringBufferLeft: Float32Array | null = null
  private ringBufferRight: Float32Array | null = null
  private ringBufferWritePtr: Int32Array | null = null
  private waveformWritePositions: Int32Array | null = null
  private waveformWritePositionsShared: Int32Array | null = null
  private waveformWritePositionsPtr = 0

  private sourcemapUrl: string
  private rpcApi: any

  constructor(options: AudioWorkletNodeOptions) {
    super()
    this.sourcemapUrl = options.processorOptions.sourcemapUrl

    // Set up RPC API
    const self = this
    const rpcApi = {
      async init(buffers: SharedBuffers): Promise<{ wasmMemory?: SharedArrayBuffer }> {
        self.reader = new BufferReader(buffers)
        self.control = new Int32Array(buffers.control)
        self.workletProgram = new Float64Array(buffers.workletProgram)
        self.workletProgramA = new Float64Array(buffers.workletProgramA)
        self.workletProgramB = new Float64Array(buffers.workletProgramB)
        self.workletSequences = buffers.workletSequences.map(
          buf => new Float64Array(buf),
        )
        // Initialize ring buffers
        self.ringBufferLeft = new Float32Array(buffers.ringBufferLeft)
        self.ringBufferRight = new Float32Array(buffers.ringBufferRight)
        self.ringBufferWritePtr = new Int32Array(buffers.ringBufferControl)
        Atomics.store(self.ringBufferWritePtr, 0, 0)
        // Initialize waveform write positions shared buffer
        self.waveformWritePositionsShared = new Int32Array(buffers.waveformWritePositions)
        // Set pointer in Wasm if already loaded
        if (self.wasm && self.waveformWritePositionsPtr !== 0) {
          self.setWaveformWritePositionsPtr()
        }
        // Initialize oversampling from control buffer if WASM is already loaded
        if (self.wasm && self.control) {
          const initialOversampling = Atomics.load(self.control, ControlIndex.OVERSAMPLING)
          if (initialOversampling !== self.currentOversampling) {
            self.currentOversampling = initialOversampling
            const baseSampleRate = sampleRate
            const effectiveSampleRate = baseSampleRate * initialOversampling
            self.wasm.wasm.sampleRate.value = effectiveSampleRate
            self.wasm.wasm.nyquist.value = effectiveSampleRate * 0.5
            self.wasm.wasm.oversamplingFactor.value = initialOversampling
            console.log(
              `[Worklet] Initial oversampling set: x${initialOversampling}, sampleRate=${effectiveSampleRate}`,
            )
          }
        }
        return {
          wasmMemory: self.wasm?.memory.buffer as SharedArrayBuffer | undefined,
        }
      },
      async loadWasm(binary: ArrayBuffer): Promise<{ wasmMemory: SharedArrayBuffer }> {
        await self.loadWasm(binary)
        return {
          wasmMemory: self.wasm!.memory.buffer as unknown as SharedArrayBuffer,
        }
      },
      async loadProgram(data: SingleProgramData | DualProgramData): Promise<void> {
        await self.loadProgramInternal(data, false)
      },
      async loadNewProgram(data: SingleProgramData | DualProgramData): Promise<void> {
        await self.loadProgramInternal(data, true)
      },
      async getSequencerPointers(): Promise<Array<{ seqId: number; statePtr: number; sequenceId: number }>> {
        return self.getSequencerPointers()
      },
      async getWaveformPointers(): Promise<Array<{ waveformId: number; bufferPtr: number; writePosition: number }>> {
        return self.getWaveformPointers()
      },
      async loadIR(irId: number, irData: ArrayBuffer, length: number): Promise<void> {
        const isDualChannel = self.control
          ? Atomics.load(self.control, ControlIndex.DUAL_CHANNEL_MODE) === 1
          : false
        self.loadImpulseResponse(irId, irData, length, isDualChannel)
      },
      async loadSample(sampleId: number, sampleData: ArrayBuffer, length: number, contentHash: number): Promise<void> {
        const isDualChannel = self.control
          ? Atomics.load(self.control, ControlIndex.DUAL_CHANNEL_MODE) === 1
          : false
        self.loadSample(sampleId, sampleData, length, contentHash, isDualChannel)
      },
      async start(): Promise<void> {
        // Start is handled by bufferManager.start() on main thread
      },
      async stop(): Promise<void> {
        // Stop is handled by bufferManager.stop() on main thread
      },
    }

    // Create RPC handler
    rpc(this.port, rpcApi)
  }

  private getSequencerPointers(): Array<{ seqId: number; statePtr: number; sequenceId: number }> {
    if (!this.wasm) {
      return []
    }

    const pointers: Array<{ seqId: number; statePtr: number; sequenceId: number }> = []

    // Iterate through possible sequencer IDs (0 to 1000)
    for (let seqId = 0; seqId < 1000; seqId++) {
      const statePtr = this.wasm.wasm.getSequencerStatePtr(seqId)
      if (statePtr === 0) continue

      const sequenceId = this.wasm.wasm.getSequencerSequenceId(seqId)
      if (sequenceId < 0) continue

      pointers.push({ seqId, statePtr, sequenceId })
    }

    return pointers
  }

  private getWaveformPointers(): Array<{ waveformId: number; bufferPtr: number; writePosition: number }> {
    if (!this.wasm) {
      return []
    }

    const pointers: Array<{ waveformId: number; bufferPtr: number; writePosition: number }> = []

    // Iterate through all possible waveform IDs (like getSequencerPointers)
    // Waveform buffers are pre-allocated, so we can check each one
    const maxWaveforms = 16 // MAX_WAVEFORMS from constants.ts
    for (let waveformId = 0; waveformId < maxWaveforms; waveformId++) {
      const bufferPtr = this.wasm.wasm.getWaveformBufferPtr?.(waveformId) ?? 0
      if (bufferPtr === 0) continue

      const writePosition = this.wasm.wasm.getWaveformWritePosition?.(waveformId) ?? 0
      pointers.push({ waveformId, bufferPtr, writePosition })
    }

    return pointers
  }

  private async loadProgramInternal(data: SingleProgramData | DualProgramData, resetState: boolean): Promise<void> {
    if (!this.wasm) {
      console.error('[Worklet] WASM not initialized')
      return
    }

    const isDualChannel = 'programA' in data

    console.log(`[Worklet] ${resetState ? 'Loading NEW program' : 'Hot-reloading program'} (dual: ${isDualChannel})`)

    // Reset state if requested
    if (resetState) {
      console.log('[Worklet] Resetting all DSP state')

      // Reset global sample counter
      this.globalSampleCounter = 0
      if (this.wasm.wasm.globalSampleCounter) {
        this.wasm.wasm.globalSampleCounter.value = 0
      }

      // Clear all samples
      if (this.wasm.wasm.clearAllSamples) {
        this.wasm.wasm.clearAllSamples()
      }
      if (isDualChannel) {
        if (this.wasm.wasm.clearAllSamplesA) this.wasm.wasm.clearAllSamplesA()
        if (this.wasm.wasm.clearAllSamplesB) this.wasm.wasm.clearAllSamplesB()
      }

      // Reset all DSP blocks
      if (this.wasm.wasm.resetAllDSPBlocks) {
        this.wasm.wasm.resetAllDSPBlocks()
      }
      if (isDualChannel) {
        if (this.wasm.wasm.resetAllDSPBlocksA) this.wasm.wasm.resetAllDSPBlocksA()
        if (this.wasm.wasm.resetAllDSPBlocksB) this.wasm.wasm.resetAllDSPBlocksB()
      }

      console.log('[Worklet] ✓ State reset complete')
    }

    if (isDualChannel) {
      await this.loadDualProgram(data as DualProgramData)
    }
    else {
      await this.loadSingleProgram(data as SingleProgramData)
    }

    console.log(`[Worklet] ✓ Program loaded`)
  }

  private async loadSingleProgram(data: SingleProgramData): Promise<void> {
    if (!this.wasm) return

    // Set BPM before loading sequences so they use the correct timing
    this.wasm.wasm.bpm.value = data.bpm

    // Load impulse responses
    if (data.impulseResponses) {
      for (const ir of data.impulseResponses) {
        this.loadImpulseResponse(ir.irId, ir.data, ir.length, false)
      }
    }

    // Load samples
    if (data.samples) {
      for (const sample of data.samples) {
        this.loadSample(sample.sampleId, sample.data, sample.length, sample.contentHash, false)
      }
    }

    // Load sequences (BPM is already set above, so they'll use correct timing)
    if (data.sequences) {
      for (const seq of data.sequences) {
        const ptr = this.sequenceBufferPtrs[seq.slotIndex]
        const len = seq.data.length
        if (len > this.sequenceBufferSize) {
          console.error(`[Worklet] Sequence too large: ${len} > ${this.sequenceBufferSize}`)
          continue
        }
        new Float64Array(this.wasm.memory.buffer, ptr, len).set(seq.data)
        this.wasm.wasm.loadSequence(ptr, len)
      }
    }

    // Load program
    if (!data.bytecode || data.bytecode.length === 0) {
      console.error('[Worklet] Program bytecode is empty')
      return
    }
    const progLen = data.bytecode.length
    if (progLen > this.programBufferSize) {
      console.error(`[Worklet] Program too large: ${progLen} > ${this.programBufferSize}`)
      return
    }
    new Float64Array(this.wasm.memory.buffer, this.programBufferPtr, progLen).set(data.bytecode)
    // Sync current globalSampleCounter to WASM before loadProgram so it can adjust it if BPM changed
    if (this.wasm.wasm.globalSampleCounter) {
      this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
    }
    this.wasm.wasm.loadProgram(this.programBufferPtr, progLen, data.tune, data.bpm)
    // Read back adjusted globalSampleCounter from WASM (loadProgram may have adjusted it if BPM changed)
    if (this.wasm.wasm.globalSampleCounter) {
      this.globalSampleCounter = this.wasm.wasm.globalSampleCounter.value
    }

    // Update control buffer to single channel mode and write BPM
    if (this.control) {
      Atomics.store(this.control, ControlIndex.DUAL_CHANNEL_MODE, 0)
      // Write BPM to control buffer so main thread can read it via getCurrentProgram()
      console.log(
        `[Worklet.loadSingleProgram] Writing BPM to control buffer: ${data.bpm} (hasExplicitBpm=${data.hasExplicitBpm})`,
      )
      Atomics.store(this.control, ControlIndex.BPM, Math.floor(data.bpm * 100))
      // Set BPM lock based on whether program has explicit BPM
      Atomics.store(this.control, ControlIndex.BPM_LOCK_SINGLE, data.hasExplicitBpm ? 1 : 0)
      const readBack = Atomics.load(this.control, ControlIndex.BPM) / 100
      console.log(`[Worklet.loadSingleProgram] Read back BPM from control buffer: ${readBack}`)
    }
    // Don't set this.lastBpm here - let the process loop initialize it from control buffer
    // This ensures the updateBpm() path fires on the first process call
  }

  private async loadDualProgram(data: DualProgramData): Promise<void> {
    if (!this.wasm) return

    // Load all impulse responses (merged from both programs)
    const allIRs = [
      ...(data.programA.impulseResponses || []),
      ...(data.programB.impulseResponses || []),
    ]
    for (const ir of allIRs) {
      this.loadImpulseResponse(ir.irId, ir.data, ir.length, true)
    }

    // Load all samples (merged from both programs)
    const allSamples = [
      ...(data.programA.samples || []),
      ...(data.programB.samples || []),
    ]
    for (const sample of allSamples) {
      this.loadSample(sample.sampleId, sample.data, sample.length, sample.contentHash, true)
    }

    // Update BPM for both executors BEFORE loading sequences
    if (this.wasm.wasm.updateBpmA && this.wasm.wasm.updateBpmB) {
      this.wasm.wasm.bpm.value = data.globalBpm
      this.wasm.wasm.updateBpmA()
      this.wasm.wasm.updateBpmB()
    }

    // Set global sample counter before loading sequences
    if (this.wasm.wasm.setGlobalSampleCounterA && this.wasm.wasm.setGlobalSampleCounterB) {
      this.wasm.wasm.setGlobalSampleCounterA(this.globalSampleCounter)
      this.wasm.wasm.setGlobalSampleCounterB(this.globalSampleCounter)
    }

    // Load sequences A
    if (data.programA.sequences) {
      for (const seq of data.programA.sequences) {
        const ptr = this.sequenceBufferPtrs[seq.slotIndex]
        const len = seq.data.length
        if (len > this.sequenceBufferSize) {
          console.error(`[Worklet] Sequence A too large: ${len} > ${this.sequenceBufferSize}`)
          continue
        }
        new Float64Array(this.wasm.memory.buffer, ptr, len).set(seq.data)
        this.wasm.wasm.loadSequenceA(ptr, len)
      }
    }

    // Load sequences B
    if (data.programB.sequences) {
      for (const seq of data.programB.sequences) {
        const ptr = this.sequenceBufferPtrs[seq.slotIndex]
        const len = seq.data.length
        if (len > this.sequenceBufferSize) {
          console.error(`[Worklet] Sequence B too large: ${len} > ${this.sequenceBufferSize}`)
          continue
        }
        new Float64Array(this.wasm.memory.buffer, ptr, len).set(seq.data)
        this.wasm.wasm.loadSequenceB(ptr, len)
      }
    }

    // Load program A
    if (!data.programA.bytecode || data.programA.bytecode.length === 0) {
      console.error('[Worklet] Program A bytecode is empty')
      return
    }
    const progLenA = data.programA.bytecode.length
    if (progLenA > this.programBufferSize) {
      console.error(`[Worklet] Program A too large: ${progLenA} > ${this.programBufferSize}`)
      return
    }
    new Float64Array(this.wasm.memory.buffer, this.programBufferPtrA, progLenA).set(data.programA.bytecode)
    this.wasm.wasm.bpm.value = data.globalBpm
    this.wasm.wasm.loadProgramA(this.programBufferPtrA, progLenA, data.tune, data.globalBpm)

    // Load program B
    if (!data.programB.bytecode || data.programB.bytecode.length === 0) {
      console.error('[Worklet] Program B bytecode is empty')
      return
    }
    const progLenB = data.programB.bytecode.length
    if (progLenB > this.programBufferSize) {
      console.error(`[Worklet] Program B too large: ${progLenB} > ${this.programBufferSize}`)
      return
    }
    new Float64Array(this.wasm.memory.buffer, this.programBufferPtrB, progLenB).set(data.programB.bytecode)
    this.wasm.wasm.bpm.value = data.globalBpm
    this.wasm.wasm.loadProgramB(this.programBufferPtrB, progLenB, data.tune, data.globalBpm)

    // Update tracking
    this.lastBpmA = data.globalBpm
    this.lastBpmB = data.globalBpm
    this.lastTuneA = data.tune
    this.lastTuneB = data.tune

    // Update control buffer to dual channel mode
    if (this.control) {
      Atomics.store(this.control, ControlIndex.DUAL_CHANNEL_MODE, 1)
      Atomics.store(this.control, ControlIndex.CROSSFADE, Math.floor(data.crossfade * 100))
    }
  }

  private async loadWasm(binary: ArrayBuffer) {
    this.wasm = await wasmSetup<typeof WasmExports>({
      binary,
      config,
      sourcemapUrl: this.sourcemapUrl,
    })

    this.wasm.wasm.createExecutor(sampleRate)
    this.wasm.wasm.createExecutorA(sampleRate)
    this.wasm.wasm.createExecutorB(sampleRate)

    const leftPtr = this.wasm.wasm.createFloat32Buffer(128)
    const rightPtr = this.wasm.wasm.createFloat32Buffer(128)
    const leftPtrA = this.wasm.wasm.createFloat32Buffer(128)
    const rightPtrA = this.wasm.wasm.createFloat32Buffer(128)
    const leftPtrB = this.wasm.wasm.createFloat32Buffer(128)
    const rightPtrB = this.wasm.wasm.createFloat32Buffer(128)

    this.leftBuffer = new Float32Array(this.wasm.memory.buffer, leftPtr, 128)
    this.rightBuffer = new Float32Array(this.wasm.memory.buffer, rightPtr, 128)
    this.leftBufferA = new Float32Array(this.wasm.memory.buffer, leftPtrA, 128)
    this.rightBufferA = new Float32Array(this.wasm.memory.buffer, rightPtrA, 128)
    this.leftBufferB = new Float32Array(this.wasm.memory.buffer, leftPtrB, 128)
    this.rightBufferB = new Float32Array(this.wasm.memory.buffer, rightPtrB, 128)

    this.limiter = new Limiter(sampleRate)
    this.downsample = new Downsample()
    this.leftMixed = new Float32Array(128)
    this.rightMixed = new Float32Array(128)

    // Preallocate oversampled buffers (128 * 8 = 1024 samples max)
    this.leftOversampledBufferPtr = this.wasm.wasm.createFloat32Buffer(128 * 8)
    this.rightOversampledBufferPtr = this.wasm.wasm.createFloat32Buffer(128 * 8)
    this.leftOversampledBuffer = new Float32Array(this.wasm.memory.buffer, this.leftOversampledBufferPtr, 128 * 8)
    this.rightOversampledBuffer = new Float32Array(this.wasm.memory.buffer, this.rightOversampledBufferPtr, 128 * 8)

    // Preallocate downsampled output buffer
    this.downsampleOutputBuffer = new Float32Array(128)

    // Preallocate program buffers (16K Float64s = 128KB)
    this.programBufferSize = 1024 * 16
    this.programBufferPtr = this.wasm.wasm.__new(this.programBufferSize * 8, 0)
    this.programBufferPtrA = this.wasm.wasm.__new(this.programBufferSize * 8, 0)
    this.programBufferPtrB = this.wasm.wasm.__new(this.programBufferSize * 8, 0)

    // Preallocate 64 sequence buffers (16K Float64s each = 128KB) - 32 for A, 32 for B
    this.sequenceBufferSize = 1024 * 16
    this.sequenceBufferPtrs = []
    for (let i = 0; i < 64; i++) {
      this.sequenceBufferPtrs.push(this.wasm.wasm.__new(this.sequenceBufferSize * 8, 0))
    }

    // Allocate space in Wasm memory for waveform write positions (16 Int32s = 64 bytes)
    this.waveformWritePositionsPtr = this.wasm.wasm.__new(16 * 4, 0)
    this.waveformWritePositions = new Int32Array(this.wasm.memory.buffer, this.waveformWritePositionsPtr, 16)
    // Initialize to zero
    this.waveformWritePositions.fill(0)
    // Set pointer in Wasm executors
    this.setWaveformWritePositionsPtr()

    // Initialize oversampling from control buffer if it's already available
    if (this.control) {
      const initialOversampling = Atomics.load(this.control, ControlIndex.OVERSAMPLING)
      if (initialOversampling !== this.currentOversampling) {
        this.currentOversampling = initialOversampling
        const baseSampleRate = sampleRate
        const effectiveSampleRate = baseSampleRate * initialOversampling
        this.wasm.wasm.sampleRate.value = effectiveSampleRate
        this.wasm.wasm.nyquist.value = effectiveSampleRate * 0.5
        this.wasm.wasm.oversamplingFactor.value = initialOversampling
        console.log(
          `[Worklet] Initial oversampling set after WASM load: x${initialOversampling}, sampleRate=${effectiveSampleRate}`,
        )
      }
    }
  }

  private loadImpulseResponse(irId: number, irData: ArrayBuffer, length: number, isDualChannel: boolean): void {
    if (!this.wasm) return

    const channelData = new Float32Array(irData)
    const irPtr = this.wasm.wasm.getTransferBuffer(length)
    const irBuffer = new Float32Array(this.wasm.memory.buffer, irPtr, length)
    irBuffer.set(channelData)

    // Always load into main executor
    this.wasm.wasm.loadImpulseResponse(irId, irPtr, length)

    // Load into dual channel executors if needed
    if (isDualChannel && this.wasm.wasm.loadImpulseResponseA && this.wasm.wasm.loadImpulseResponseB) {
      this.wasm.wasm.loadImpulseResponseA(irId, irPtr, length)
      this.wasm.wasm.loadImpulseResponseB(irId, irPtr, length)
    }
  }

  private loadSample(sampleId: number, sampleData: ArrayBuffer, length: number, contentHash: number,
    isDualChannel: boolean): void
  {
    if (!this.wasm) return

    const channelData = new Float32Array(sampleData)
    const samplePtr = this.wasm.wasm.getTransferBuffer(length)
    const sampleBuffer = new Float32Array(this.wasm.memory.buffer, samplePtr, length)
    sampleBuffer.set(channelData)

    const contentHashI32 = contentHash | 0

    // Always load into main executor
    this.wasm.wasm.loadSample(sampleId, samplePtr, length, contentHashI32)
    if (this.wasm.wasm.loadSlicerSample) {
      this.wasm.wasm.loadSlicerSample(sampleId, samplePtr, length, contentHashI32)
    }

    // Load into dual channel executors if needed
    if (isDualChannel) {
      if (this.wasm.wasm.loadSampleA) {
        this.wasm.wasm.loadSampleA(sampleId, samplePtr, length, contentHashI32)
      }
      if (this.wasm.wasm.loadSampleB) {
        this.wasm.wasm.loadSampleB(sampleId, samplePtr, length, contentHashI32)
      }
      if (this.wasm.wasm.loadSlicerSampleA) {
        this.wasm.wasm.loadSlicerSampleA(sampleId, samplePtr, length, contentHashI32)
      }
      if (this.wasm.wasm.loadSlicerSampleB) {
        this.wasm.wasm.loadSlicerSampleB(sampleId, samplePtr, length, contentHashI32)
      }
    }
  }

  private writeToRingBuffer(left: Float32Array, right: Float32Array, blockSize: number): void {
    if (!this.ringBufferLeft || !this.ringBufferRight || !this.ringBufferWritePtr) return

    const writePtr = Atomics.load(this.ringBufferWritePtr, 0)

    for (let i = 0; i < blockSize; i++) {
      const bufferIndex = (writePtr + i) % RING_BUFFER_SIZE
      this.ringBufferLeft[bufferIndex] = left[i]
      this.ringBufferRight[bufferIndex] = right[i]
    }

    const newWritePtr = (writePtr + blockSize) % RING_BUFFER_SIZE
    Atomics.store(this.ringBufferWritePtr, 0, newWritePtr)
  }

  private setWaveformWritePositionsPtr(): void {
    if (!this.wasm || this.waveformWritePositionsPtr === 0) return

    // Set pointer for main executor
    if (this.wasm.wasm.setWaveformWritePositionsSharedPtr) {
      this.wasm.wasm.setWaveformWritePositionsSharedPtr(this.waveformWritePositionsPtr)
    }
    // Set pointer for channel A executor
    if (this.wasm.wasm.setWaveformWritePositionsSharedPtrA) {
      this.wasm.wasm.setWaveformWritePositionsSharedPtrA(this.waveformWritePositionsPtr)
    }
    // Set pointer for channel B executor
    if (this.wasm.wasm.setWaveformWritePositionsSharedPtrB) {
      this.wasm.wasm.setWaveformWritePositionsSharedPtrB(this.waveformWritePositionsPtr)
    }
  }

  private syncWaveformWritePositions(): void {
    if (!this.waveformWritePositions || !this.waveformWritePositionsShared) return

    // Copy from Wasm memory to shared buffer
    this.waveformWritePositionsShared.set(this.waveformWritePositions)
  }

  process(
    inputs: Float32Array[][],
    outputs: Float32Array[][],
    parameters: Record<string, Float32Array>,
  ): boolean {
    if (!outputs[0] || !this.wasm || !this.reader || !this.leftBuffer || !this.rightBuffer) {
      return true
    }

    // Check for BPM and tune changes
    if (this.control) {
      const isDualChannel = Atomics.load(this.control, ControlIndex.DUAL_CHANNEL_MODE) === 1
      if (isDualChannel) {
        const currentBpm = Atomics.load(this.control, ControlIndex.GLOBAL_BPM) / 100
        const shouldUpdateA = this.lastBpmA === -1 || currentBpm !== this.lastBpmA
        const shouldUpdateB = this.lastBpmB === -1 || currentBpm !== this.lastBpmB
        if (shouldUpdateA || shouldUpdateB) {
          // Sync current globalSampleCounter to WASM before updateBpm so it uses the correct value
          // Both executors share the same globalSampleCounter
          if (this.wasm.wasm.globalSampleCounter) {
            this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
          }
          this.wasm.wasm.bpm.value = currentBpm
          if (shouldUpdateA && this.wasm.wasm.updateBpmA) {
            this.wasm.wasm.updateBpmA()
          }
          if (shouldUpdateB && this.wasm.wasm.updateBpmB) {
            this.wasm.wasm.updateBpmB()
          }
          // Read back adjusted globalSampleCounter from WASM (it may have been adjusted to maintain beat position)
          // Both executors share the same globalSampleCounter, so we only need to read once
          if (this.wasm.wasm.globalSampleCounter) {
            this.globalSampleCounter = this.wasm.wasm.globalSampleCounter.value
          }
        }
        this.lastBpmA = currentBpm
        this.lastBpmB = currentBpm

        const currentTune = Atomics.load(this.control, ControlIndex.TUNE) / 1000
        if (currentTune !== this.lastTuneA && this.lastTuneA !== -1) {
          if (this.wasm.wasm.updateTuneA) {
            this.wasm.wasm.updateTuneA(currentTune)
          }
        }
        if (currentTune !== this.lastTuneB && this.lastTuneB !== -1) {
          if (this.wasm.wasm.updateTuneB) {
            this.wasm.wasm.updateTuneB(currentTune)
          }
        }
        this.lastTuneA = currentTune
        this.lastTuneB = currentTune
      }
      else {
        // Always read and update BPM from control buffer
        // BPM_LOCK_SINGLE just prevents setGlobalBpm() from overwriting program BPM,
        // but the worklet should still update WASM when program BPM changes
        const currentBpm = Atomics.load(this.control, ControlIndex.BPM) / 100
        // Check if BPM changed (or if lastBpm is -1, meaning we need to initialize)
        if (this.lastBpm === -1 || currentBpm !== this.lastBpm) {
          // Sync current globalSampleCounter to WASM before updateBpm so it uses the correct value
          if (this.wasm.wasm.globalSampleCounter) {
            this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
          }
          this.wasm.wasm.bpm.value = currentBpm
          // Always call updateBpm to ensure sequencer timing is correct
          // On first init, this updates timing with the correct BPM (sequences were loaded before loadProgram)
          // On BPM change, this adjusts positions and updates timing
          this.wasm.wasm.updateBpm()
          // Read back adjusted globalSampleCounter from WASM (it may have been adjusted to maintain beat position)
          if (this.wasm.wasm.globalSampleCounter) {
            this.globalSampleCounter = this.wasm.wasm.globalSampleCounter.value
          }
          this.lastBpm = currentBpm
        }
      }
    }

    // Check for oversampling changes
    if (this.control && this.wasm) {
      const newOversampling = Atomics.load(this.control, ControlIndex.OVERSAMPLING)
      if (newOversampling !== this.currentOversampling) {
        this.currentOversampling = newOversampling
        const baseSampleRate = sampleRate
        const effectiveSampleRate = baseSampleRate * newOversampling
        this.wasm.wasm.sampleRate.value = effectiveSampleRate
        this.wasm.wasm.nyquist.value = effectiveSampleRate * 0.5
        this.wasm.wasm.oversamplingFactor.value = newOversampling
      }
    }

    // Set global sample counter before processing
    if (this.wasm.wasm.globalSampleCounter) {
      this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
    }

    // Process audio if playing
    if (this.reader.isPlaying()) {
      const isDualChannel = this.control
        ? Atomics.load(this.control, ControlIndex.DUAL_CHANNEL_MODE) === 1
        : false

      if (
        isDualChannel
        && this.leftBufferA
        && this.rightBufferA
        && this.leftBufferB
        && this.rightBufferB
      ) {
        if (!this.dualChannelLoggedOnce) {
          const crossfade = this.control
            ? Atomics.load(this.control, ControlIndex.CROSSFADE) / 100
            : 0.5
          console.log('[Worklet] 🎵 Processing in DUAL CHANNEL mode', { crossfade })
          this.dualChannelLoggedOnce = true
        }

        // Sync global sample counter before processing
        // This ensures sequences stay in sync between channels
        // Set counter before EACH processBlock call to ensure both A and B see the same value
        // when sequences initialize their patternStartSample
        if (this.wasm.wasm.globalSampleCounter) {
          this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
        }

        // Process both channels with oversampling
        if (this.currentOversampling > 1 && this.leftOversampledBuffer && this.rightOversampledBuffer) {
          // Process channel A: generate oversampled output
          for (let i = 0; i < this.currentOversampling; i++) {
            // Reset counter before each block to ensure A and B see the same value
            if (this.wasm.wasm.globalSampleCounter) {
              this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter + i * BLOCK_SIZE
            }
            const offset = i * BLOCK_SIZE
            const leftPtr = this.leftOversampledBufferPtr + offset * 4
            const rightPtr = this.rightOversampledBufferPtr + offset * 4
            this.wasm.wasm.processBlockA(leftPtr, rightPtr)
          }
          // Downsample channel A
          const oversampledSize = BLOCK_SIZE * this.currentOversampling
          if (this.downsampleOutputBuffer && this.downsample) {
            this.downsample.process(
              this.leftOversampledBuffer.subarray(0, oversampledSize),
              this.downsampleOutputBuffer,
              BLOCK_SIZE,
              this.currentOversampling,
            )
            this.leftBufferA.set(this.downsampleOutputBuffer)
            this.downsample.process(
              this.rightOversampledBuffer.subarray(0, oversampledSize),
              this.downsampleOutputBuffer,
              BLOCK_SIZE,
              this.currentOversampling,
            )
            this.rightBufferA.set(this.downsampleOutputBuffer)
          }

          // Process channel B: generate oversampled output
          // Reset counter to same value as A to ensure sequences initialize with same patternStartSample
          if (this.wasm.wasm.globalSampleCounter) {
            this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
          }
          for (let i = 0; i < this.currentOversampling; i++) {
            // Reset counter before each block to ensure A and B see the same value
            if (this.wasm.wasm.globalSampleCounter) {
              this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter + i * BLOCK_SIZE
            }
            const offset = i * BLOCK_SIZE
            const leftPtr = this.leftOversampledBufferPtr + offset * 4
            const rightPtr = this.rightOversampledBufferPtr + offset * 4
            this.wasm.wasm.processBlockB(leftPtr, rightPtr)
          }
          // Downsample channel B
          if (this.downsampleOutputBuffer && this.downsample) {
            this.downsample.process(
              this.leftOversampledBuffer.subarray(0, oversampledSize),
              this.downsampleOutputBuffer,
              BLOCK_SIZE,
              this.currentOversampling,
            )
            this.leftBufferB.set(this.downsampleOutputBuffer)
            this.downsample.process(
              this.rightOversampledBuffer.subarray(0, oversampledSize),
              this.downsampleOutputBuffer,
              BLOCK_SIZE,
              this.currentOversampling,
            )
            this.rightBufferB.set(this.downsampleOutputBuffer)
          }
        }
        else {
          // Process channel A
          if (this.wasm.wasm.globalSampleCounter) {
            this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
          }
          this.wasm.wasm.processBlockA(this.leftBufferA.byteOffset, this.rightBufferA.byteOffset)

          // Process channel B - reset counter to same value as A
          if (this.wasm.wasm.globalSampleCounter) {
            this.wasm.wasm.globalSampleCounter.value = this.globalSampleCounter
          }
          this.wasm.wasm.processBlockB(this.leftBufferB.byteOffset, this.rightBufferB.byteOffset)
        }

        // Get crossfade value
        const crossfade = this.control
          ? Atomics.load(this.control, ControlIndex.CROSSFADE) / 100
          : 0.5
        const gainA = crossfade <= 0.5 ? 1.0 : 2.0 * (1.0 - crossfade)
        const gainB = crossfade >= 0.5 ? 1.0 : 2.0 * crossfade

        // Mix outputs based on crossfade
        if (this.leftMixed && this.rightMixed && this.limiter) {
          for (let i = 0; i < BLOCK_SIZE; i++) {
            this.leftMixed[i] = this.leftBufferA[i] * gainA + this.leftBufferB[i] * gainB
            this.rightMixed[i] = this.rightBufferA[i] * gainA + this.rightBufferB[i] * gainB
          }

          // Apply limiter to final mix
          this.limiter.process(this.leftMixed, this.rightMixed, BLOCK_SIZE)

          // Copy to output
          outputs[0][0].set(this.leftMixed)
          outputs[0][1].set(this.rightMixed)

          // Write to ring buffer for visualizers
          this.writeToRingBuffer(this.leftMixed, this.rightMixed, BLOCK_SIZE)
        }
      }
      else {
        if (!this.dualChannelLoggedOnce) {
          console.log('[Worklet] 🎵 Processing in SINGLE CHANNEL mode')
          this.dualChannelLoggedOnce = true
        }

        // Single channel mode with oversampling
        if (this.currentOversampling > 1 && this.leftOversampledBuffer && this.rightOversampledBuffer) {
          // Generate oversampled output
          for (let i = 0; i < this.currentOversampling; i++) {
            const offset = i * BLOCK_SIZE
            const leftPtr = this.leftOversampledBufferPtr + offset * 4
            const rightPtr = this.rightOversampledBufferPtr + offset * 4
            this.wasm.wasm.processBlock(leftPtr, rightPtr)
          }
          // Downsample back to 128 samples
          const oversampledSize = BLOCK_SIZE * this.currentOversampling
          if (this.downsampleOutputBuffer && this.downsample) {
            this.downsample.process(
              this.leftOversampledBuffer.subarray(0, oversampledSize),
              this.downsampleOutputBuffer,
              BLOCK_SIZE,
              this.currentOversampling,
            )
            outputs[0][0].set(this.downsampleOutputBuffer)
            this.downsample.process(
              this.rightOversampledBuffer.subarray(0, oversampledSize),
              this.downsampleOutputBuffer,
              BLOCK_SIZE,
              this.currentOversampling,
            )
            outputs[0][1].set(this.downsampleOutputBuffer)
            // Write to ring buffer (use output buffers which are already downsampled)
            this.writeToRingBuffer(outputs[0][0], outputs[0][1], BLOCK_SIZE)
          }
        }
        else {
          this.wasm.wasm.processBlock(this.leftBuffer.byteOffset, this.rightBuffer.byteOffset)
          outputs[0][0].set(this.leftBuffer)
          outputs[0][1].set(this.rightBuffer)
          // Write to ring buffer for visualizers
          this.writeToRingBuffer(this.leftBuffer, this.rightBuffer, BLOCK_SIZE)
        }
      }

      // Global sample counter is incremented by WASM during processing
    }

    // Read back global sample counter from WASM (it was incremented during processing)
    if (this.wasm.wasm.globalSampleCounter) {
      this.globalSampleCounter = this.wasm.wasm.globalSampleCounter.value
    }

    // Publish global sample counter to main thread via SharedArrayBuffer
    // (publish even when not playing so main thread can read the last value)
    if (this.control) {
      Atomics.store(this.control, ControlIndex.GLOBAL_SAMPLE_COUNTER, this.globalSampleCounter)
    }

    // Sync waveform write positions to shared buffer
    this.syncWaveformWritePositions()

    return true
  }
}

if (typeof AudioWorkletProcessor !== 'undefined') {
  registerProcessor('dspscript-processor', loopmasterProcessorWasm)
}
